/**
 * Who plays whom in a picture book whose story has a `baby` role (new-sibling: "Becomes the Big One").
 *
 * The hero of such a story is the child BECOMING a big sibling, so a newborn in the family photo must never be the
 * hero, whatever the parent ticked as "main" (the people step marks the first detected face as main by default, and
 * that face is often the baby in someone's arms). Rule:
 *   1. The baby is the youngest "baby of the cast": a child under 2, or a person whose relationship says
 *      baby/newborn/infant (an explicit relationship counts as age 0 when no age is known).
 *   2. The hero comes from everyone else: a child aged 2-17 (marked main first, else the youngest), else a remaining
 *      child under 2 (two babies: the older one is becoming the big one), else a person of unknown age. A grown-up is
 *      never the hero of this story.
 *   3. No baby in the photo: the baby is drawn generically (the master's ifAbsent) and the hero is chosen as above.
 *   4. No possible hero (a newborn alone, or a newborn with grown-ups only): the template does not fit this cast and
 *      is refused/not offered (pictureBookTemplateFitsCast), because a story about becoming the big sibling has no
 *      one to tell it about, and making the newborn the "big one" would be the very bug this module prevents.
 *
 * Pure and import-free: safe for client bundles and workers.
 */

/** Templates whose story has a `baby` role (kept equal to the master scripts' roles by casting.test.ts). */
export const BABY_ROLE_TEMPLATES: ReadonlySet<string> = new Set(['new-sibling']);

export type CastPerson = {
  name: string;
  age?: number | string | null;
  isMainCharacter?: boolean;
  relationship?: string | null;
};

// The relationship is about the person being the baby ("baby sister", "our new baby", "newborn"), never about
// someone else's baby ("baby's grandma", "grandma of the baby") or a word that only contains it ("babysitter").
const BABY_WORD = /^\s*(?:(?:the|our|my|a|his|her|their)\s+)?(?:new[\s-]?born|new\s+baby|baby|infant)(?![\w'\u2019])/i;

/**
 * Age in years from what the UI, the face analysis or a parent may give: a number, a numeric string, "6 months",
 * "3 weeks", "2 years", or a baby word ("newborn", "baby", "infant" = 0). Anything else (empty, negative, text) is
 * undefined (unknown).
 */
export function ageInYears(age: unknown): number | undefined {
  if (typeof age === 'number') return Number.isFinite(age) && age >= 0 ? age : undefined;
  if (typeof age !== 'string') return undefined;
  const s = age.trim().toLowerCase();
  if (!s) return undefined;
  if (/^(new[\s-]?born|baby|infant)$/.test(s)) return 0;
  const m = /^(\d+(?:\.\d+)?)\s*(y|yr|yrs|year|years|m|mo|mos|month|months|w|wk|wks|week|weeks)?\.?(?:\s*old)?$/.exec(s);
  if (!m) return undefined;
  const n = Number(m[1]);
  const unit = m[2] ?? 'y';
  if (unit.startsWith('w')) return n / 52;
  if (unit.startsWith('m')) return n / 12;
  return n;
}

/** A child under 2, or a child (or a person of unknown age) whose relationship says baby/newborn/infant. */
export function isBabyOfCast(p: CastPerson): boolean {
  const a = ageInYears(p.age);
  if (a !== undefined && a < 2) return true;
  if (a !== undefined && a >= 18) return false;
  return typeof p.relationship === 'string' && BABY_WORD.test(p.relationship);
}

/** Age for ordering babies: an explicit baby relationship with no known age counts as 0. */
const babyAge = (p: CastPerson): number => ageInYears(p.age) ?? 0;

function youngestBy<T>(list: T[], age: (p: T) => number): T {
  return list.reduce((a, b) => (age(b) < age(a) ? b : a));
}

/** Marked main first (youngest of those), else the youngest. */
function pickHero<T extends CastPerson>(list: T[], age: (p: T) => number): T | undefined {
  if (!list.length) return undefined;
  const main = list.filter((p) => p.isMainCharacter);
  return youngestBy(main.length ? main : list, age);
}

/** The cast of a baby-role story: { hero, baby }; either may be undefined (see the module rule). */
export function castBabyRoleStory<T extends CastPerson>(cs: T[]): { hero?: T; baby?: T } {
  const babies = cs.filter(isBabyOfCast);
  const baby = babies.length ? youngestBy<T>(babies, babyAge) : undefined;
  const rest = cs.filter((p) => p !== baby);
  const known = (p: T) => ageInYears(p.age) as number;
  const olderKids = rest.filter((p) => {
    const a = ageInYears(p.age);
    return a !== undefined && a >= 2 && a < 18 && !isBabyOfCast(p);
  });
  const babyKids = rest.filter(isBabyOfCast);
  const unknownAge = rest.filter((p) => ageInYears(p.age) === undefined && !isBabyOfCast(p));
  const hero =
    pickHero<T>(olderKids, known) ??
    pickHero<T>(babyKids, babyAge) ??
    (unknownAge.find((p) => p.isMainCharacter) ?? unknownAge[0]);
  return { hero, baby };
}

/** Whether a template can be made for this cast: a baby-role template needs a hero who is not the baby. */
export function pictureBookTemplateFitsCast(templateId: string | undefined | null, cs: CastPerson[]): boolean {
  if (!templateId || !BABY_ROLE_TEMPLATES.has(templateId)) return true;
  return castBabyRoleStory(cs).hero !== undefined;
}

/**
 * Shown when a baby-role story cannot be made for this cast (no child to become the big sibling). User-facing:
 * copy approved by the owner 2026-10-29.
 */
export const TEMPLATE_CAST_REFUSAL =
  'This story is about a child becoming a big brother or sister, so it needs an older child in the photo. Please pick another story.';

/**
 * The whole-year age a baby-role (new-sibling) create request sends for a person: a newborn or a baby under 1 is
 * 0, "18 months" is 1, a relationship-marked baby with no age is 0; only a missing or unreadable age falls back to
 * `fallback`. Other templates and comics keep their old rule (Number(age) || 8), untouched.
 */
export function castAge(p: CastPerson, fallback = 8): number {
  const a = ageInYears(p.age);
  if (a === undefined) return isBabyOfCast(p) ? 0 : fallback;
  return Math.floor(a);
}

/** Relationship stored for the cast's baby in a baby-role book, so the story writer casts them as the baby. */
export const BABY_RELATIONSHIP = 'new baby sibling';

/**
 * The cast as stored for a baby-role book: exactly the cast hero is the main character and the baby is labelled
 * BABY_RELATIONSHIP. Null when the template does not fit (no hero). Other templates: the cast unchanged.
 */
export function castForTemplate<T extends CastPerson>(templateId: string | undefined | null, cs: T[]): T[] | null {
  if (!templateId || !BABY_ROLE_TEMPLATES.has(templateId)) return cs;
  const { hero, baby } = castBabyRoleStory(cs);
  if (!hero) return null;
  return cs.map((p) => ({
    ...p,
    isMainCharacter: p === hero,
    ...(p === baby ? { relationship: BABY_RELATIONSHIP } : {}),
  }));
}
